import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import { API_URLS } from "@/config/urls";
import type {
  AuthRecoveryResult,
  ExecuteAuthFlowOptions,
} from "@/hooks/useAuthFlow";
import { extractUploadQuotaExceededDetail } from "@/lib/apiErrors";
import { getLatestOidcToken } from "@/lib/authSession";
import { getShowOriginalFromStorage } from "@/lib/displayPreferences";
import i18n from "@/lib/i18n";
import { emitUploadQuotaExceeded } from "@/lib/uploadQuotaEvents";

export const api = axios.create({
  baseURL: API_URLS.BASE_API,
  headers: {
    "Content-Type": "application/json",
  },
});

export const wolfApi = axios.create({
  baseURL: API_URLS.WOLF_BACKEND,
  headers: {
    "Content-Type": "application/json",
  },
});

// 認証フロー関数の型定義
interface AuthFlow {
  executeAuthFlow: (
    options?: ExecuteAuthFlowOptions,
  ) => Promise<AuthRecoveryResult>;
}

type AuthAwareRequestConfig = InternalAxiosRequestConfig & {
  _isRetry?: boolean;
  skipAuthFlow?: boolean;
  omitAuthCredentials?: boolean;
};

// 認証フローハンドラーを格納する変数
let authFlowHandler: AuthFlow | null = null;
let authRecoveryPromise: Promise<AuthRecoveryResult> | null = null;

/**
 * 認証フローハンドラーを設定
 */
export const setAuthFlowHandler = (handler: AuthFlow) => {
  authFlowHandler = handler;
};

const executeSharedAuthRecovery = () => {
  if (authRecoveryPromise) {
    return authRecoveryPromise;
  }

  if (!authFlowHandler) {
    return null;
  }

  let sharedPromise: Promise<AuthRecoveryResult>;
  sharedPromise = authFlowHandler
    .executeAuthFlow({ allowRedirect: true })
    .finally(() => {
      if (authRecoveryPromise === sharedPromise) {
        authRecoveryPromise = null;
      }
    });
  authRecoveryPromise = sharedPromise;
  return sharedPromise;
};

const isSafeToReplay = (method?: string) =>
  ["get", "head", "options"].includes((method ?? "get").toLowerCase());

const attachAuthInterceptors = (
  client: AxiosInstance,
  options: { useOidcBearerToken?: boolean } = {},
) => {
  client.interceptors.request.use(
    (config) => {
      if ((config as AuthAwareRequestConfig).omitAuthCredentials) {
        config.withCredentials = false;
        delete config.headers?.Authorization;
        return config;
      }

      // クッキーを含むようにする
      config.withCredentials = true;

      if ((config.method ?? "get").toLowerCase() === "get") {
        const language = (i18n.resolvedLanguage ?? i18n.language ?? "en").split(
          "-",
        )[0];
        config.params = {
          display_language: language,
          ...config.params,
        };

        if (
          getShowOriginalFromStorage() &&
          config.params.show_original == null
        ) {
          config.params.show_original = "1";
        }
      }

      if (options.useOidcBearerToken) {
        const token = getLatestOidcToken()?.token;
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
      } else if (import.meta.env.DEV) {
        // 開発環境の場合、クッキーからトークンを取得してAuthorizationヘッダーに設定
        const token = getAuthTokenFromCookie();
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as
        | AuthAwareRequestConfig
        | undefined;
      const quotaDetail = extractUploadQuotaExceededDetail(
        error.response?.data,
      );
      if (quotaDetail !== null) {
        emitUploadQuotaExceeded(quotaDetail);
      }

      // 401エラーかつ未リトライの場合
      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._isRetry &&
        !originalRequest.skipAuthFlow
      ) {
        // OIDCログインエンドポイントでの401エラーは再試行しない
        // （OIDCトークン自体が無効なため）
        if (
          originalRequest.url?.includes(API_URLS.HUMHUB_OAUTH_LOGIN) ||
          originalRequest.url?.includes("oidc-login")
        ) {
          return Promise.reject(error);
        }

        // 認証フローハンドラーが設定されている場合
        if (authFlowHandler) {
          // リトライフラグを設定
          originalRequest._isRetry = true;

          try {
            // 認証フローを実行
            const recoveryPromise = executeSharedAuthRecovery();
            const authResult = recoveryPromise
              ? await recoveryPromise
              : { status: "retry_later", reason: "auth_flow_unavailable" };

            if (
              authResult.status === "recovered" &&
              isSafeToReplay(originalRequest.method)
            ) {
              // クッキーを含むようにリクエストを設定
              originalRequest.withCredentials = true;

              // リクエストを再実行
              return client(originalRequest);
            }

            if (
              authResult.status === "reauth_required" &&
              authResult.reason === "invalid_refresh_token"
            ) {
              return Promise.reject(
                new Error("Authentication required - sign-in is required"),
              );
            }
          } catch (authError) {
            console.error(" Auth flow error:", authError);
            return Promise.reject(authError);
          }
        }
      }

      return Promise.reject(error);
    },
  );
};

// 開発環境でクッキーからトークンを取得する関数
const getAuthTokenFromCookie = (): string | null => {
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith("humhub_auth_token=")) {
      const token = cookie.substring("humhub_auth_token=".length);
      return token;
    }
  }
  return null;
};

attachAuthInterceptors(api);
attachAuthInterceptors(wolfApi, { useOidcBearerToken: true });

export default api;
